import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { storage, formatDateTime, formatDate } from '@/lib/storage';
import { STATUS_OPTIONS, SAMPLE_REQUESTS } from '@/data/mockData';
import { Search, FileCheck, Eye, Trash2, Filter, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const ManageRequests = () => {
  const [data, setData] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('All');
  const [view, setView] = useState(null);

  useEffect(() => {
    let stored = storage.get('requests', null);
    if (stored === null) {
      // Seed sample on first load
      const seeded = SAMPLE_REQUESTS.map((r) => ({
        ...r,
        submittedAt: new Date(r.date).toISOString(),
        timeline: [{ status: r.status, date: new Date(r.date).toISOString(), note: 'Sample seed entry.' }],
      }));
      storage.set('requests', seeded);
      stored = seeded;
    }
    setData(stored);
  }, []);

  const list = useMemo(() => {
    return data.filter((r) => {
      const matchStatus = status === 'All' || r.status === status;
      const matchQ = (r.id + r.type + (r.name || '') + (r.email || '')).toLowerCase().includes(q.toLowerCase());
      return matchStatus && matchQ;
    });
  }, [data, q, status]);

  const updateStatus = (id, newStatus) => {
    const arr = storage.get('requests', []);
    const next = arr.map((r) => {
      if (r.id !== id) return r;
      return {
        ...r,
        status: newStatus,
        timeline: [
          ...(r.timeline || []),
          { status: newStatus, date: new Date().toISOString(), note: `Status updated to ${newStatus}.` },
        ],
      };
    });
    storage.set('requests', next);
    setData(next);
    toast.success(`Status updated to ${newStatus}`);
    if (view && view.id === id) setView(next.find((x) => x.id === id));
  };

  const remove = (id) => {
    if (!window.confirm('Delete this request? This cannot be undone.')) return;
    const next = data.filter((r) => r.id !== id);
    storage.set('requests', next);
    setData(next);
    toast.success('Request deleted');
  };

  const exportCsv = () => {
    const headers = ['Reference', 'Service', 'Name', 'Email', 'Purpose', 'Status', 'Submitted'];
    const rows = list.map((r) => [
      r.id, r.type, r.name || '', r.email || '', (r.purpose || '').replace(/[\n,]/g, ' '), r.status, formatDateTime(r.submittedAt || r.date),
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `requests-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div data-testid="admin-requests-page">
      <AdminPageHeader
        title="Service Requests"
        description="Review, update, and manage document requests submitted by residents."
        actions={
          <Button variant="outline" onClick={exportCsv} data-testid="requests-export">
            <FileDown className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <Card className="p-4 sm:p-5 mb-5 border-0 shadow-sm-soft">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by reference, name, email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="requests-search"
            />
          </div>
          <div className="flex gap-2 items-center">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[200px]" data-testid="requests-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All statuses</SelectItem>
                {STATUS_OPTIONS.request.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-md-soft overflow-hidden">
        {list.length === 0 ? (
          <div className="p-6"><EmptyState icon={FileCheck} title="No requests" description="No requests match the current filter." /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Resident</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((r) => (
                  <TableRow key={r.id} data-testid={`row-request-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.id}</TableCell>
                    <TableCell>{r.type}</TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{r.name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.email || ''}</div>
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground" title={r.purpose}>{r.purpose || '—'}</TableCell>
                    <TableCell>
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="w-[180px] h-8 text-xs" data-testid={`status-trigger-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.request.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(r.date)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => setView(r)} data-testid={`view-request-${r.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r.id)} className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-request-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={Boolean(view)} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Request · {view.id}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <Field label="Service">{view.type}</Field>
                  <Field label="Status"><StatusBadge status={view.status} /></Field>
                  <Field label="Name">{view.name}</Field>
                  <Field label="Email">{view.email}</Field>
                  <Field label="Mobile">{view.contact || '—'}</Field>
                  <Field label="Age / Civil">{view.age} · {view.civil || '—'}</Field>
                  <Field label="Address" full>{view.address || '—'}</Field>
                  <Field label="Purpose" full>{view.purpose}</Field>
                </div>
                {view.timeline && view.timeline.length > 0 && (
                  <div>
                    <h4 className="font-heading font-semibold mb-2">Timeline</h4>
                    <ol className="relative border-l-2 border-border pl-5 space-y-3">
                      {view.timeline.map((t, i) => (
                        <li key={i} className="relative">
                          <span className="absolute -left-[27px] top-1 h-3.5 w-3.5 rounded-full bg-primary ring-4 ring-background" />
                          <div className="text-sm font-semibold">{t.status}</div>
                          <div className="text-xs text-muted-foreground">{formatDateTime(t.date)}</div>
                          {t.note && <div className="mt-0.5 text-xs text-muted-foreground">{t.note}</div>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-0.5">{children || '—'}</div>
  </div>
);

export default ManageRequests;
